import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Report.css';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import AvgValueChart from '../../components/AvgValueChart';
// Acomodar la asignación de vfs para evitar runtime errors si la estructura difiere
pdfMake.vfs = (pdfFonts && pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) ? pdfFonts.pdfMake.vfs : (pdfFonts && pdfFonts.pdfMake) ? pdfFonts.pdfMake : pdfFonts;

const REPORT_METRIC_GROUPS = [
  {
    key: 'summary',
    label: 'resumen principal',
    metrics: ['average_hourly_price', 'average_daily_price', 'min_max_hourly_price'],
  },
  {
    key: 'providers',
    label: 'proveedores',
    metrics: ['provider_hourly_price'],
  },
  {
    key: 'locations',
    label: 'ubicaciones',
    metrics: ['top_locations'],
  },
];

function createBaseReport(sessionId, recordCount = 0) {
  return {
    success: true,
    session_id: sessionId,
    record_count: recordCount,
    metrics: {},
    errors: null,
  };
}

function mergeReportChunk(previousReport, nextChunk, sessionId, fallbackRecordCount = 0) {
  const baseReport = previousReport || createBaseReport(sessionId, fallbackRecordCount);
  return {
    ...baseReport,
    success: true,
    session_id: nextChunk?.session_id || baseReport.session_id || sessionId,
    record_count: nextChunk?.record_count ?? baseReport.record_count ?? fallbackRecordCount,
    metrics: {
      ...(baseReport.metrics || {}),
      ...(nextChunk?.metrics || {}),
    },
    errors: null,
  };
}

function Report() {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [providerSearchTerm, setProviderSearchTerm] = useState('');
  const [groupStatus, setGroupStatus] = useState({});
  const [groupErrors, setGroupErrors] = useState({});

  // Filtrar proveedores según el término de búsqueda
  const filteredProviders = useMemo(() => {
    if (!reportData?.metrics?.provider_hourly_price?.providers) return [];
    
    if (!providerSearchTerm.trim()) {
      return reportData.metrics.provider_hourly_price.providers;
    }

    const searchLower = providerSearchTerm.toLowerCase().trim();
    return reportData.metrics.provider_hourly_price.providers.filter(provider => 
      provider.provider_name.toLowerCase().includes(searchLower)
    );
  }, [reportData, providerSearchTerm]);

  useEffect(() => {
    const storedSession = sessionStorage.getItem('analysisSession');
    
    if (!storedSession) {
      navigate('/dashboard');
      return;
    }

    let sessionId;
    let recordCount = 0;
    let reportCacheKey;
    try {
      const parsedSession = JSON.parse(storedSession);
      sessionId = parsedSession.sessionId;
      recordCount = parsedSession.recordCount || 0;
      reportCacheKey = `analysisReport:${sessionId}`;
    } catch (err) {
      console.error('Error al parsear sesión:', err);
      navigate('/dashboard');
      return;
    }

    let cancelled = false;

    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      setGroupStatus({});
      setGroupErrors({});

      let cachedMetrics = new Set();
      const baseReport = createBaseReport(sessionId, recordCount);

      const cachedReport = sessionStorage.getItem(reportCacheKey);
      if (cachedReport) {
        try {
          const parsedReport = JSON.parse(cachedReport);
          if (parsedReport?.success && parsedReport?.session_id === sessionId) {
            if (!cancelled) {
              setReportData(mergeReportChunk(null, parsedReport, sessionId, recordCount));
            }
            cachedMetrics = new Set(Object.keys(parsedReport.metrics || {}));
          } else {
            sessionStorage.removeItem(reportCacheKey);
          }
        } catch (cacheError) {
          sessionStorage.removeItem(reportCacheKey);
        }
      }

      if (!cancelled) {
        setReportData((current) => current || baseReport);
      }

      const groupsToFetch = REPORT_METRIC_GROUPS.filter((group) =>
        group.metrics.some((metric) => !cachedMetrics.has(metric))
      );

      if (groupsToFetch.length === 0) {
        if (!cancelled) {
          setLoading(false);
        }
        return;
      }

      if (!cancelled) {
        setGroupStatus((current) => {
          const next = { ...current };
          groupsToFetch.forEach((group) => {
            next[group.key] = 'loading';
          });
          return next;
        });
      }

      const failures = [];
      let loadedAnyGroup = cachedMetrics.size > 0;

      await Promise.all(
        groupsToFetch.map(async (group) => {
          try {
            const response = await fetch(
              `/api/analytics/report/${sessionId}/?metrics=${group.metrics.join(',')}`
            );

            if (!response.ok) {
              throw new Error(`Error ${response.status}: No se pudo cargar ${group.label}`);
            }

            const data = await response.json();
            if (!data.success) {
              throw new Error(data.error || `No se pudo cargar ${group.label}`);
            }

            if (cancelled) {
              return;
            }

            setReportData((current) => {
              const mergedReport = mergeReportChunk(current, data, sessionId, recordCount);
              sessionStorage.setItem(reportCacheKey, JSON.stringify(mergedReport));
              return mergedReport;
            });
            loadedAnyGroup = true;
            setGroupStatus((current) => ({ ...current, [group.key]: 'success' }));
            setGroupErrors((current) => {
              const next = { ...current };
              delete next[group.key];
              return next;
            });
          } catch (groupError) {
            failures.push(groupError);
            if (!cancelled) {
              setGroupStatus((current) => ({ ...current, [group.key]: 'error' }));
              setGroupErrors((current) => ({
                ...current,
                [group.key]: groupError.message,
              }));
            }
          }
        })
      );

      if (!cancelled) {
        if (failures.length === groupsToFetch.length && !loadedAnyGroup) {
          setError('No se pudo cargar el reporte. Intenta nuevamente en unos segundos.');
        }
        setLoading(false);
      }
    };

    fetchReport();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const generatePDF = async () => {
    if (!reportData) {
      console.error('No hay datos para generar el PDF.');
      return;
    }

    // Intentar capturar la imagen del gráfico si está renderizado
    let chartImage = null;
    try {
      if (chartRef && chartRef.current && typeof chartRef.current.toBase64Image === 'function') {
        chartImage = chartRef.current.toBase64Image();
      }
    } catch (err) {
      // ignorar
      // eslint-disable-next-line no-console
      console.warn('No se pudo capturar la imagen del gráfico:', err);
      chartImage = null;
    }
    // Funciones auxiliares locales para usar dentro del PDF
    const fmtNum = (n) => new Intl.NumberFormat('es-CO').format(n);
    const fmtCur = (n, cur = 'COP') => new Intl.NumberFormat('es-CO', {
      style: 'currency', currency: cur, minimumFractionDigits: 2, maximumFractionDigits: 2
    }).format(n);

    const content = [];

    // Encabezado
    content.push({ text: 'REPORTE DE ANÁLISIS', style: 'header' });
    content.push({ text: `Total de registros analizados: ${fmtNum(reportData.record_count)}`, style: 'subheader' });
    content.push({ text: '\n' });

    // Precios Promedio
    if (metrics.average_hourly_price || metrics.average_daily_price) {
      const avgColumns = [];
      if (metrics.average_hourly_price) {
        avgColumns.push({
          width: 'auto',
          stack: [
            { text: 'Por Hora', style: 'tableHeader' },
            { text: fmtCur(metrics.average_hourly_price.average_hourly_price, metrics.average_hourly_price.currency), style: 'large' },
            { text: 'Precio promedio por hora de trabajo', style: 'muted' }
          ]
        });
      }
      if (metrics.average_daily_price) {
        avgColumns.push({
          width: 'auto',
          stack: [
            { text: 'Por Día', style: 'tableHeader' },
            { text: fmtCur(metrics.average_daily_price.average_daily_price, metrics.average_daily_price.currency), style: 'large' },
            { text: 'Precio promedio por día de trabajo', style: 'muted' }
          ]
        });
      }

      content.push({ columns: avgColumns, columnGap: 20, margin: [0, 0, 0, 10] });

      content.push({
        columns: [
          { width: '*', text: 'Se tomó a consideración que 1 día laboral son 8 horas y se trabajan 5 días a la semana', style: 'note' }
        ],
        margin: [0, 0, 0, 10]
      });
    }

    // Min / Max
    if (metrics.min_max_hourly_price) {
      const mm = metrics.min_max_hourly_price;
      const mmCols = [];

      if (mm.min_hourly_price) {
        mmCols.push({
          width: 'auto',
          stack: [
            { text: 'Precio Más Bajo', style: 'tableHeader' },
            { text: fmtCur(mm.min_hourly_price.hourly_price, mm.currency), style: 'large' },
            { text: `Proveedor: ${mm.min_hourly_price.proveedor}`, style: 'muted' },
            { text: `ID Proceso: ${mm.min_hourly_price.id_proceso}`, style: 'muted' }
          ]
        });
      }

      if (mm.max_hourly_price) {
        mmCols.push({
          width: 'auto',
          stack: [
            { text: 'Precio Más Alto', style: 'tableHeader' },
            { text: fmtCur(mm.max_hourly_price.hourly_price, mm.currency), style: 'large' },
            { text: `Proveedor: ${mm.max_hourly_price.proveedor}`, style: 'muted' },
            { text: `ID Proceso: ${mm.max_hourly_price.id_proceso}`, style: 'muted' }
          ]
        });
      }

      content.push({ columns: mmCols, columnGap: 20, margin: [0, 0, 0, 10] });

      if (mm.price_range !== undefined) {
        content.push({ text: `Rango de precios: ${fmtCur(mm.price_range, mm.currency)}`, style: 'content', margin: [0, 0, 0, 10] });
      }
      // Si no se obtuvo imagen desde el ref, intentar capturar el canvas directamente
      if (!chartImage) {
        try {
          const canvas = document.querySelector('.avg-value-chart canvas');
          if (canvas && canvas.toDataURL) {
            chartImage = canvas.toDataURL('image/png');
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('No se pudo capturar canvas del gráfico:', err);
        }
      }

      // Insertar imagen del gráfico en el PDF si está disponible (después de Min/Max)
      if (chartImage) {
        content.push({ text: 'Gráfico: Valor promedio por tiempo', style: 'sectionHeader' });
        content.push({ image: chartImage, width: 450, margin: [0, 6, 0, 12] });
      }
    }

    // Proveedores (usar el filtro actual mostrado en pantalla)
    if (metrics.provider_hourly_price && metrics.provider_hourly_price.providers && metrics.provider_hourly_price.providers.length > 0) {
      const providers = filteredProviders; // ya calculado en el componente

      content.push({ text: 'Precio Promedio por Hora por Proveedor', style: 'sectionHeader' });
      content.push({ text: `Total de proveedores: ${metrics.provider_hourly_price.total_providers} · Mostrando: ${providers.length}`, style: 'muted', margin: [0, 0, 0, 6] });

      const providerTableBody = [
        [ { text: 'Proveedor', style: 'tableHeader' }, { text: 'Contratos', style: 'tableHeader' }, { text: 'Precio / hora', style: 'tableHeader' } ]
      ];

      providers.forEach((p) => {
        providerTableBody.push([
          { text: p.provider_name },
          { text: p.contract_count.toString(), alignment: 'right' },
          { text: fmtCur(p.average_hourly_price, metrics.provider_hourly_price.currency), alignment: 'right' }
        ]);
      });

      content.push({ table: { headerRows: 1, widths: ['*', 60, 100], body: providerTableBody }, layout: 'lightHorizontalLines', margin: [0, 0, 0, 10] });
    }

    // Top Locations
    if (metrics.top_locations) {
      content.push({ text: 'Distribución Geográfica', style: 'sectionHeader' });
      content.push({ text: `Departamentos: ${metrics.top_locations.total_departments} · Ciudades: ${metrics.top_locations.total_cities}`, style: 'muted', margin: [0, 0, 0, 6] });

      // Departamentos
      if (metrics.top_locations.top_departments && metrics.top_locations.top_departments.length > 0) {
        const deptTable = [ [ { text: 'Rank', style: 'tableHeader' }, { text: 'Departamento', style: 'tableHeader' }, { text: 'Conteo', style: 'tableHeader' }, { text: '%', style: 'tableHeader' } ] ];
        metrics.top_locations.top_departments.forEach((d, i) => {
          deptTable.push([ (i+1).toString(), d.name, fmtNum(d.count), `${d.percentage}%` ]);
        });
        content.push({ text: 'Top 5 Departamentos', style: 'tableSubheader', margin: [0, 6, 0, 4] });
        content.push({ table: { headerRows: 1, widths: [30, '*', 80, 50], body: deptTable }, layout: 'lightHorizontalLines', margin: [0, 0, 0, 10] });
      }

      // Ciudades
      if (metrics.top_locations.top_cities && metrics.top_locations.top_cities.length > 0) {
        const cityTable = [ [ { text: 'Rank', style: 'tableHeader' }, { text: 'Ciudad', style: 'tableHeader' }, { text: 'Conteo', style: 'tableHeader' }, { text: '%', style: 'tableHeader' } ] ];
        metrics.top_locations.top_cities.forEach((c, i) => {
          cityTable.push([ (i+1).toString(), c.name, fmtNum(c.count), `${c.percentage}%` ]);
        });
        content.push({ text: 'Top 5 Ciudades', style: 'tableSubheader', margin: [0, 6, 0, 4] });
        content.push({ table: { headerRows: 1, widths: [30, '*', 80, 50], body: cityTable }, layout: 'lightHorizontalLines', margin: [0, 0, 0, 10] });
      }
    }

    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 60, 40, 60],
      content,
      styles: {
        header: { fontSize: 18, bold: true, alignment: 'center', margin: [0, 0, 0, 10] },
        subheader: { fontSize: 12, alignment: 'center', margin: [0, 0, 0, 10] },
        sectionHeader: { fontSize: 14, bold: true, margin: [0, 6, 0, 6] },
        tableHeader: { bold: true, fontSize: 12 },
        large: { fontSize: 16, bold: true },
        muted: { color: '#555', fontSize: 10 },
        note: { italics: true, fontSize: 10 },
        content: { fontSize: 11 }
      },
      defaultStyle: { fontSize: 11 }
    };

    pdfMake.createPdf(docDefinition).open();
  };

  if (loading && !reportData) {
    return (
      <div className="report-container">
        <div className="report-loading">
          <div className="spinner"></div>
          <span>Generando reporte...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-container">
        <div className="report-error">
          <h2>Error al cargar el reporte</h2>
          <p>{error}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => navigate('/dashboard')}
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Formatear número con separadores de miles
  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-CO').format(num);
  };

  // Formatear moneda
  const formatCurrency = (num, currency = 'COP') => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const metrics = reportData?.metrics || {};
  const isGroupLoading = (groupKey) => groupStatus[groupKey] === 'loading';
  const getGroupError = (groupKey) => groupErrors[groupKey];

  // Detectar series para gráfico (priorizar hourly_series, luego daily_series)
  const avgSeries = (metrics.average_hourly_price && metrics.average_hourly_price.series) ?
    metrics.average_hourly_price.series.map(s => ({ label: s.label || s.period || s.hour || s.date, value: s.value || s.average })) :
    (metrics.average_daily_price && metrics.average_daily_price.series) ?
    metrics.average_daily_price.series.map(s => ({ label: s.label || s.period || s.date, value: s.value || s.average })) :
    [];

  // Log de diagnóstico para ayudar a entender por qué no se renderiza el gráfico
  // (Se puede eliminar después de depurar)
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('Report metrics:', metrics);
    // eslint-disable-next-line no-console
    console.log('Derived avgSeries for chart:', avgSeries);
  }

  return (
    <div className="report-container">
      <div className="report-header">
        <h1 className="report-title">REPORTE DE ANÁLISIS</h1>
        <div className="header-buttons">
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            ← Volver al Dashboard
          </button>
          <button 
            className="btn btn-primary"
            onClick={generatePDF}
          >
            Generar reporte en PDF
          </button>
        </div>
      </div>

      <div className="report-content">
        {loading && (
          <div className="report-inline-status">
            Cargando secciones del reporte. Verás cada bloque apenas esté listo.
          </div>
        )}

        {Object.keys(groupErrors).length > 0 && (
          <div className="report-inline-status report-inline-status-warning">
            Algunas secciones no se pudieron cargar en este intento. Puedes volver a entrar al reporte y el sistema reutilizará lo que ya quedó cacheado.
          </div>
        )}

        {/* Información general de la sesión */}
        <div className="report-section">
          <h2 className="section-title">Información General</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Total de registros analizados:</span>
              <span className="info-value highlight">{formatNumber(reportData.record_count)}</span>
            </div>
          </div>
        </div>

        {/* Métricas de Precio Promedio */}
        {(metrics.average_hourly_price || metrics.average_daily_price || isGroupLoading('summary') || getGroupError('summary')) && (
          <div className="report-section metric-section">
            <h2 className="section-title">Precios Promedio</h2>
            {!metrics.average_hourly_price && !metrics.average_daily_price && isGroupLoading('summary') && (
              <div className="report-section-status">Cargando métricas principales...</div>
            )}
            {!metrics.average_hourly_price && !metrics.average_daily_price && getGroupError('summary') && (
              <div className="report-section-status report-section-status-error">{getGroupError('summary')}</div>
            )}
            <div className="metrics-grid">
              {/* Precio Promedio por Hora */}
              {metrics.average_hourly_price && (
                <div className="metric-card">
                  <h3 className="metric-card-title">Por Hora</h3>
                  <div className="metric-content">
                    <div className="metric-highlight">
                      <div className="metric-value-large">
                        {formatCurrency(metrics.average_hourly_price.average_hourly_price, metrics.average_hourly_price.currency)}
                      </div>
                      <div className="metric-label">Precio promedio por hora de trabajo</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Precio Promedio por Día */}
              {metrics.average_daily_price && (
                <div className="metric-card">
                  <h3 className="metric-card-title">Por Día</h3>
                  <div className="metric-content">
                    <div className="metric-highlight">
                      <div className="metric-value-large">
                        {formatCurrency(metrics.average_daily_price.average_daily_price, metrics.average_daily_price.currency)}
                      </div>
                      <div className="metric-label">Precio promedio por día de trabajo</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Nota compartida para ambas métricas */}
            <div className="info-note">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
              </svg>
              <span>Se tomó a consideración que 1 día laboral son 8 horas y se trabajan 5 días a la semana</span>
            </div>
          </div>
        )}

        {/* Métrica: Precio Mínimo y Máximo por Hora */}
        {(metrics.min_max_hourly_price || isGroupLoading('summary') || getGroupError('summary')) && (
          <div className="report-section metric-section">
            <h2 className="section-title">Precio Mínimo y Máximo por Hora</h2>
            {!metrics.min_max_hourly_price && isGroupLoading('summary') && (
              <div className="report-section-status">Cargando extremos de precio por hora...</div>
            )}
            {!metrics.min_max_hourly_price && getGroupError('summary') && (
              <div className="report-section-status report-section-status-error">{getGroupError('summary')}</div>
            )}
            {metrics.min_max_hourly_price && (
            <div className="metric-content">
              <div className="min-max-grid">
                {/* Precio Mínimo */}
                {metrics.min_max_hourly_price.min_hourly_price && (
                  <div className="price-card min-price-card">
                    <div className="price-header">
                      <h3>Precio Más Bajo</h3>
                    </div>
                    <div className="price-value">
                      {formatCurrency(metrics.min_max_hourly_price.min_hourly_price.hourly_price, metrics.min_max_hourly_price.currency)}
                      <span className="price-label">por hora</span>
                    </div>
                    <div className="price-details">
                      <div className="detail-item">
                        <div>
                          <strong>Proveedor:</strong><br />
                          {metrics.min_max_hourly_price.min_hourly_price.proveedor}
                        </div>
                      </div>
                      <div className="detail-item">
                        <div>
                          <strong>ID del Proceso:</strong><br />
                          {metrics.min_max_hourly_price.min_hourly_price.id_proceso}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Precio Máximo */}
                {metrics.min_max_hourly_price.max_hourly_price && (
                  <div className="price-card max-price-card">
                    <div className="price-header">
                      <h3>Precio Más Alto</h3>
                    </div>
                    <div className="price-value">
                      {formatCurrency(metrics.min_max_hourly_price.max_hourly_price.hourly_price, metrics.min_max_hourly_price.currency)}
                      <span className="price-label">por hora</span>
                    </div>
                    <div className="price-details">
                      <div className="detail-item">
                        <div>
                          <strong>Proveedor:</strong><br />
                          {metrics.min_max_hourly_price.max_hourly_price.proveedor}
                        </div>
                      </div>
                      <div className="detail-item">
                        <div>
                          <strong>ID del Proceso:</strong><br />
                          {metrics.min_max_hourly_price.max_hourly_price.id_proceso}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Resumen estadístico */}
              <div className="metric-summary-single">
                <div className="summary-item-large">
                  <span className="summary-label">Rango de precios:</span>
                  <span className="summary-value-large">{formatCurrency(metrics.min_max_hourly_price.price_range, metrics.min_max_hourly_price.currency)}</span>
                </div>
              </div>
            </div>
            )}
          </div>
        )}

        {/* Gráfico de valor promedio por tiempo (solo en UI) */}
        <div className="report-section">
          <h2 className="section-title">Valor promedio por tiempo</h2>
          {avgSeries && avgSeries.length > 0 ? (
            <AvgValueChart ref={chartRef} series={avgSeries} title={'Valor promedio'} />
          ) : (
            <div className="no-chart-note">
              <p>No hay serie de datos disponible para mostrar el gráfico.</p>
              <p className="muted">Si esperas ver el gráfico, revisa que `metrics.average_hourly_price.series` o `metrics.average_daily_price.series` estén presentes en la respuesta del backend.</p>
            </div>
          )}
        </div>

        {/* Precio por Hora por Proveedor */}
        {(metrics.provider_hourly_price || isGroupLoading('providers') || getGroupError('providers')) && (
          <div className="report-section metric-section">
            <h2 className="section-title">Precio Promedio por Hora por Proveedor</h2>
            {!metrics.provider_hourly_price && isGroupLoading('providers') && (
              <div className="report-section-status">Cargando análisis por proveedor...</div>
            )}
            {!metrics.provider_hourly_price && getGroupError('providers') && (
              <div className="report-section-status report-section-status-error">{getGroupError('providers')}</div>
            )}
            
            {metrics.provider_hourly_price && metrics.provider_hourly_price.providers && metrics.provider_hourly_price.providers.length > 0 && (
            <div className="metric-content">
              {/* Header con búsqueda */}
              <div className="provider-header">
                <div className="provider-stats">
                  <span className="stat-item">
                    <span className="stat-label">Total de proveedores:</span>
                    <span className="stat-value">{metrics.provider_hourly_price.total_providers}</span>
                  </span>
                  <span className="stat-item">
                    <span className="stat-label">Mostrando:</span>
                    <span className="stat-value">{filteredProviders.length}</span>
                  </span>
                </div>
                
                <div className="provider-search-wrapper">
                  <input
                    type="text"
                    className="provider-search-input"
                    placeholder="Buscar proveedor..."
                    value={providerSearchTerm}
                    onChange={(e) => setProviderSearchTerm(e.target.value)}
                  />
                  <svg 
                    className="search-icon-provider" 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="18" 
                    height="18" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </div>
              </div>

              {/* Lista de proveedores */}
              <div className="provider-list-container">
                {filteredProviders.length > 0 ? (
                  <div className="provider-list">
                    {filteredProviders.map((provider, index) => (
                      <div key={index} className="provider-item">
                        <div className="provider-info">
                          <div className="provider-name">{provider.provider_name}</div>
                          <div className="provider-contracts">
                            {provider.contract_count} contrato{provider.contract_count !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <div className="provider-price">
                          <span className="price-amount">{formatCurrency(provider.average_hourly_price, metrics.provider_hourly_price.currency)}</span>
                          <span className="price-unit">/ hora</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-results">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="48" 
                      height="48" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    <p>No se encontraron proveedores que coincidan con "{providerSearchTerm}"</p>
                  </div>
                )}
              </div>
            </div>
            )}
            {metrics.provider_hourly_price && metrics.provider_hourly_price.providers && metrics.provider_hourly_price.providers.length === 0 && (
              <div className="report-section-status">No hay proveedores válidos para mostrar en este análisis.</div>
            )}
          </div>
        )}

        {/* Top 5 Departamentos y Ciudades */}
        {(metrics.top_locations || isGroupLoading('locations') || getGroupError('locations')) && (
          <div className="report-section metric-section">
            <h2 className="section-title">Distribución Geográfica</h2>
            {!metrics.top_locations && isGroupLoading('locations') && (
              <div className="report-section-status">Cargando distribución geográfica...</div>
            )}
            {!metrics.top_locations && getGroupError('locations') && (
              <div className="report-section-status report-section-status-error">{getGroupError('locations')}</div>
            )}
            
            {metrics.top_locations && (
            <div className="metric-content">
              <div className="location-stats">
                <span className="stat-badge">
                  <strong>{metrics.top_locations.total_departments}</strong> departamentos
                </span>
                <span className="stat-badge">
                  <strong>{metrics.top_locations.total_cities}</strong> ciudades
                </span>
              </div>

              <div className="locations-grid">
                {/* Top 5 Departamentos */}
                <div className="location-section">
                  <h3 className="location-section-title">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6"/>
                    </svg>
                    Top 5 Departamentos
                  </h3>
                  <div className="location-list">
                    {metrics.top_locations.top_departments && metrics.top_locations.top_departments.length > 0 ? (
                      metrics.top_locations.top_departments.map((dept, index) => (
                        <div key={index} className="location-item">
                          <div className="location-rank">{index + 1}</div>
                          <div className="location-info">
                            <div className="location-name">{dept.name}</div>
                            <div className="location-bar-container">
                              <div 
                                className="location-bar" 
                                style={{width: `${dept.percentage}%`}}
                              ></div>
                            </div>
                          </div>
                          <div className="location-stats-right">
                            <div className="location-count">{formatNumber(dept.count)}</div>
                            <div className="location-percentage">{dept.percentage}%</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="no-data">No hay datos de departamentos</p>
                    )}
                  </div>
                </div>

                {/* Top 5 Ciudades */}
                <div className="location-section">
                  <h3 className="location-section-title">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M0 11.25V12h2v2.25A2.75 2.75 0 0 0 4.75 17h6.5A2.75 2.75 0 0 0 14 14.25V12h2v-.75H0zm2.75-1.5h10.5c.69 0 1.25.56 1.25 1.25v3.75c0 .69-.56 1.25-1.25 1.25H2.75c-.69 0-1.25-.56-1.25-1.25v-3.75c0-.69.56-1.25 1.25-1.25z"/>
                      <path d="M4.5 9h7a.5.5 0 0 1 .5.5V12h-8V9.5a.5.5 0 0 1 .5-.5zM3 4a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v5H3V4zm6 0a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v5h-5V4z"/>
                    </svg>
                    Top 5 Ciudades
                  </h3>
                  <div className="location-list">
                    {metrics.top_locations.top_cities && metrics.top_locations.top_cities.length > 0 ? (
                      metrics.top_locations.top_cities.map((city, index) => (
                        <div key={index} className="location-item">
                          <div className="location-rank">{index + 1}</div>
                          <div className="location-info">
                            <div className="location-name">{city.name}</div>
                            <div className="location-bar-container">
                              <div 
                                className="location-bar" 
                                style={{width: `${city.percentage}%`}}
                              ></div>
                            </div>
                          </div>
                          <div className="location-stats-right">
                            <div className="location-count">{formatNumber(city.count)}</div>
                            <div className="location-percentage">{city.percentage}%</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="no-data">No hay datos de ciudades</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Report;
